/**
 * People on a business page (Business Account Pages slice 2, migrations 2026-10-06-310/311/313):
 *  - Points of contact (POCs): rows of contact_organizations with relationship_type = 'poc'. A person may be a POC at
 *    several organizations; linking never creates or edits the person.
 *  - Sales Account Owner: the primary row of sales_territory_assignments.
 *  - Concierge rep: organization_concierge_assignments, chosen from the Concierge team roster.
 * Only names, email, phone and job title are read for a POC -- never membership, subscription or case data.
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

/** Requirements §6.3 (must match CHECK contact_organizations_poc_role_check). */
export const POC_ROLES = [
  "Owner",
  "Manager",
  "Head Instructor",
  "Main POC",
  "Staff",
  "Billing Contact",
] as const;
export type PocRole = (typeof POC_ROLES)[number];

export interface OrganizationPoc {
  link_id: UUID;
  contact_id: UUID;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  title: string | null;
  poc_role: PocRole | null;
  is_primary: boolean;
  is_billing_contact: boolean;
}

export interface PocCandidate {
  contact_id: UUID;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  title: string | null;
  already_poc: boolean;
}

/** Operator-language text for the server's refusals. */
export const describePocError = (message?: string): string => {
  const m = message || "";
  if (m.includes("POC_EMAIL_EXISTS")) {
    return "A person with this email already exists. Use “Find existing person” to link them instead.";
  }
  if (m.includes("POC_RELATIONSHIP_CONFLICT")) {
    return "This person is already linked to this organization in another way (for example as a member). That link was left unchanged; ask an administrator to review it.";
  }
  if (m.includes("POC_REQUIRED_FIELDS")) {
    return m.includes("email")
      ? "An email address is required for a new point of contact."
      : "First and last name are required.";
  }
  if (m.includes("POC_INVALID_EMAIL")) {
    return "The email address is not valid.";
  }
  if (m.includes("POC_NOT_ALLOWED") || m.includes("row-level security")) {
    return "You do not have permission to change points of contact.";
  }
  if (m.includes("contact_organizations_one_primary_poc")) {
    return "Another primary contact was just set for this organization. Reload and try again.";
  }
  return m || "The point of contact could not be saved.";
};

export const getOrganizationPocs = async (
  organizationId: UUID,
): Promise<OrganizationPoc[]> => {
  const { data, error } = await supabase
    .from("contact_organizations")
    .select(
      "id, contact_id, poc_role, is_primary, is_billing_contact, contact:contacts(id, first_name, last_name, email, phone_number, title)",
    )
    .eq("organization_id", organizationId)
    .eq("relationship_type", "poc")
    .limit(500);
  if (error) {
    throw error;
  }
  type Row = {
    id: UUID;
    contact_id: UUID;
    poc_role: PocRole | null;
    is_primary: boolean | null;
    is_billing_contact: boolean;
    contact: {
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      phone_number: string | null;
      title: string | null;
    } | null;
  };
  const rows = ((data ?? []) as unknown as Row[]).map((r) => ({
    link_id: r.id,
    contact_id: r.contact_id,
    first_name: r.contact?.first_name ?? null,
    last_name: r.contact?.last_name ?? null,
    email: r.contact?.email ?? null,
    phone_number: r.contact?.phone_number ?? null,
    title: r.contact?.title ?? null,
    poc_role: r.poc_role,
    is_primary: r.is_primary === true,
    is_billing_contact: r.is_billing_contact === true,
  }));
  return rows.sort(
    (a, b) =>
      Number(b.is_primary) - Number(a.is_primary) ||
      `${a.last_name ?? ""} ${a.first_name ?? ""}`.localeCompare(
        `${b.last_name ?? ""} ${b.first_name ?? ""}`,
      ),
  );
};

export const searchPocCandidates = async (
  query: string,
  organizationId: UUID,
): Promise<PocCandidate[]> => {
  const { data, error } = await supabase.rpc("search_poc_candidates", {
    p_query: query,
    p_organization_id: organizationId,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as PocCandidate[];
};

export interface PocFlags {
  poc_role: PocRole | null;
  is_primary: boolean;
  is_billing_contact: boolean;
}

/** Link an existing person, or update an existing POC's role/flags. */
export const setOrganizationPoc = async (
  organizationId: UUID,
  contactId: UUID,
  flags: PocFlags,
): Promise<UUID> => {
  const { data, error } = await supabase.rpc("set_organization_poc", {
    p_organization_id: organizationId,
    p_contact_id: contactId,
    p_poc_role: flags.poc_role,
    p_is_primary: flags.is_primary,
    p_is_billing_contact: flags.is_billing_contact,
  });
  if (error) {
    throw error;
  }
  return data as UUID;
};

export interface NewPocInput extends PocFlags {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  title?: string;
}

/** Create a NEW person (email required) and link them. Refuses with POC_EMAIL_EXISTS for a known email. */
export const createOrganizationPoc = async (
  organizationId: UUID,
  input: NewPocInput,
): Promise<UUID> => {
  const { data, error } = await supabase.rpc("create_organization_poc", {
    p_organization_id: organizationId,
    p_first_name: input.first_name,
    p_last_name: input.last_name,
    p_email: input.email,
    p_phone: input.phone_number || null,
    p_title: input.title || null,
    p_poc_role: input.poc_role,
    p_is_primary: input.is_primary,
    p_is_billing_contact: input.is_billing_contact,
  });
  if (error) {
    // the existing person's id travels in DETAIL so the page can offer "link this person"
    const err = new Error(error.message) as Error & { existingId?: string };
    if (error.message.includes("POC_EMAIL_EXISTS") && error.details) {
      err.existingId = error.details;
    }
    throw err;
  }
  return data as UUID;
};

/** Removes the POC link only; the person and their links to other organizations are untouched. */
export const removeOrganizationPoc = async (linkId: UUID): Promise<void> => {
  const { error, count } = await supabase
    .from("contact_organizations")
    .delete({ count: "exact" })
    .eq("id", linkId)
    .eq("relationship_type", "poc");
  if (error) {
    throw error;
  }
  if (count === 0) {
    throw new Error("POC_NOT_ALLOWED: nothing was removed.");
  }
};

// ---------------------------------------------------------------- sales owner

export interface SalesPositionOption {
  id: UUID;
  title: string;
  assigned_name: string | null;
  position_type: string | null;
}

export const getActiveSalesPositions = async (): Promise<
  SalesPositionOption[]
> => {
  const { data, error } = await supabase
    .from("sales_positions")
    .select("id, title, assigned_name, position_type")
    .eq("is_active", true)
    .order("assigned_name", { ascending: true, nullsFirst: false })
    .limit(500);
  if (error) {
    throw error;
  }
  return (data ?? []) as SalesPositionOption[];
};

export const getOrganizationSalesOwner = async (
  organizationId: UUID,
): Promise<SalesPositionOption | null> => {
  const { data, error } = await supabase
    .from("sales_territory_assignments")
    .select(
      "sales_position:sales_positions(id, title, assigned_name, position_type)",
    )
    .eq("organization_id", organizationId)
    .eq("is_primary", true)
    .limit(1);
  if (error) {
    throw error;
  }
  const row = (data ?? [])[0] as unknown as
    | { sales_position: SalesPositionOption | null }
    | undefined;
  return row?.sales_position ?? null;
};

export const setOrganizationSalesOwner = async (
  organizationId: UUID,
  salesPositionId: UUID | null,
): Promise<void> => {
  const { error } = await supabase.rpc("set_organization_sales_owner", {
    p_organization_id: organizationId,
    p_sales_position_id: salesPositionId,
  });
  if (error) {
    throw error;
  }
};

export const salesPositionLabel = (p: SalesPositionOption): string =>
  p.assigned_name ? `${p.assigned_name} — ${p.title}` : `${p.title} (vacant)`;

// ---------------------------------------------------------------- Concierge

export interface ConciergeMember {
  profile_id: UUID;
  full_name: string | null;
  email: string | null;
}

/** The Concierge team roster (internal staff flagged by an administrator). */
export const getConciergeTeam = async (): Promise<ConciergeMember[]> => {
  const { data, error } = await supabase
    .from("concierge_team_members")
    .select(
      "profile_id, profile:profiles!concierge_team_members_profile_id_fkey(full_name, email, is_active)",
    )
    .limit(500);
  if (error) {
    throw error;
  }
  type Row = {
    profile_id: UUID;
    profile: {
      full_name: string | null;
      email: string | null;
      is_active: boolean | null;
    } | null;
  };
  return ((data ?? []) as unknown as Row[])
    .filter((r) => r.profile?.is_active !== false)
    .map((r) => ({
      profile_id: r.profile_id,
      full_name: r.profile?.full_name ?? null,
      email: r.profile?.email ?? null,
    }))
    .sort((a, b) =>
      (a.full_name || a.email || "").localeCompare(
        b.full_name || b.email || "",
      ),
    );
};

export const getOrganizationConcierge = async (
  organizationId: UUID,
): Promise<UUID | null> => {
  const { data, error } = await supabase
    .from("organization_concierge_assignments")
    .select("concierge_profile_id")
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data?.concierge_profile_id as UUID | undefined) ?? null;
};

export const setOrganizationConcierge = async (
  organizationId: UUID,
  profileId: UUID | null,
): Promise<void> => {
  if (!profileId) {
    const { error } = await supabase
      .from("organization_concierge_assignments")
      .delete()
      .eq("organization_id", organizationId);
    if (error) {
      throw error;
    }
    return;
  }
  const { error } = await supabase
    .from("organization_concierge_assignments")
    .upsert(
      {
        organization_id: organizationId,
        concierge_profile_id: profileId,
        assigned_at: new Date().toISOString(),
      },
      { onConflict: "organization_id" },
    );
  if (error) {
    throw error;
  }
};

export interface ConciergeCandidate {
  profile_id: UUID;
  full_name: string | null;
  email: string | null;
  role: string;
  on_team: boolean;
}

/** Administrators only (the server refuses everyone else). */
export const listConciergeCandidates = async (): Promise<
  ConciergeCandidate[]
> => {
  const { data, error } = await supabase.rpc("list_concierge_candidates");
  if (error) {
    throw error;
  }
  return (data ?? []) as ConciergeCandidate[];
};

export const setConciergeTeamMember = async (
  profileId: UUID,
  onTeam: boolean,
): Promise<void> => {
  const { error } = await supabase.rpc("set_concierge_team_member", {
    p_profile_id: profileId,
    p_on_team: onTeam,
  });
  if (error) {
    throw error;
  }
};
